import { type ReactNode } from 'react';

import { Check } from '@hungryroot/icons';
import { Heading, Paragraph } from '@hungryroot/ramen';
import { ColorElementPrimary, ColorElementSecondary } from '@hungryroot/tokens';

import {
  type Animation,
  type Step,
  StepBasedAnimation,
} from './StepBasedAnimation';
import styles from './Building.module.css';

interface Props<R extends string> {
  exp154Image: ReactNode;
  image: ReactNode;
  redirectPath: R;
  onComplete: (path: R) => void;
}

const LOAD_TIME = 6000;

export const messageDuration = LOAD_TIME / 4;

const CheckRow = ({ children }: { children: ReactNode }) => (
  <div className={styles['check-row']}>
    <Check className={styles.check} />
    <Paragraph variant="lead">{children}</Paragraph>
  </div>
);

type DelayAndDuration = [number, number];
type Ease = 'easeOut' | 'easeIn';

const createOpacityAnimation = (
  [delay, duration]: DelayAndDuration,
  ease: Ease = 'easeOut'
): Animation => {
  return {
    key: 'opacity',
    initial: 0,
    animate: 1,
    transition: {
      delay,
      duration,
      ease,
    },
  };
};

const createColorAnimation = ([
  delay,
  duration,
]: DelayAndDuration): Animation => {
  return {
    key: 'color',
    initial: ColorElementSecondary,
    animate: ColorElementPrimary,
    transition: {
      delay,
      duration,
      ease: 'easeOut',
    },
  };
};

const getSteps = (image: ReactNode): Step[] => [
  {
    element: 'div',
    content: () => <>{image}</>,
    animations: [createOpacityAnimation([0, 0.15], 'easeIn')],
  },
  {
    element: 'div',
    content: () => (
      <Heading variant="HeroAlt" className={styles['header']}>
        We're analyzing your preferences...
      </Heading>
    ),
    animations: [createOpacityAnimation([0, 0.3])],
  },
  {
    element: 'div',
    className: styles['check-motion'],
    content: () => <CheckRow>Capturing your tastes</CheckRow>,
    animations: [
      createOpacityAnimation([0.9, 0.3]),
      createColorAnimation([1.2, 0.3]),
    ],
  },
  {
    element: 'div',
    className: styles['check-motion'],
    content: () => <CheckRow>Reviewing your goals</CheckRow>,
    animations: [
      createOpacityAnimation([1.2, 0.3]),
      createColorAnimation([0.2, 0.3]),
    ],
  },
  {
    element: 'div',
    className: styles['check-motion'],
    content: () => <CheckRow>Building your plan</CheckRow>,
    animations: [
      createOpacityAnimation([1.2, 0.3]),
      createColorAnimation([0.2, 0.3]),
    ],
  },
];

export function Building<R extends string>(props: Props<R>) {
  return (
    <div className={styles['exp-154-builder']}>
      <StepBasedAnimation
        steps={getSteps(props.exp154Image)}
        onComplete={{
          delay: 1000,
          handler: () => props.onComplete(props.redirectPath),
        }}
      />
    </div>
  );
}

export { BUILDING_PATH } from '../../navigation/funnel-routes';
